import { useDashboardPedidos } from '@/hooks/usePedidos';
import { useEstatisticasProducao } from '@/hooks/useProducao';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { formatCurrency, formatPercentage } from '@/lib/utils';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingBag,
  Package, Printer, BarChart3, AlertTriangle, CheckCircle,
  ArrowUpRight, Zap, Target, Activity
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  icon: React.ElementType;
  iconBg: string;
  href?: string;
  highlight?: boolean;
}

function StatCard({ title, value, description, trend, trendValue, icon: Icon, iconBg, href, highlight }: StatCardProps) {
  const content = (
    <Card className={cn(
      'hover:shadow-elevated transition-all cursor-pointer border',
      highlight ? 'border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10' : 'border-gray-100'
    )}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className={cn('p-3 rounded-xl', iconBg)}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          {trend && trendValue && (
            <div className={cn(
              'flex items-center gap-1 text-sm font-bold px-2 py-0.5 rounded-full',
              trend === 'up' ? 'text-green-700 bg-green-50' : trend === 'down' ? 'text-red-700 bg-red-50' : 'text-gray-600 bg-gray-50'
            )}>
              {trend === 'up' ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {trendValue}
            </div>
          )}
        </div>
        <div className="mt-4">
          <h3 className={cn('text-2xl font-black', highlight ? 'text-primary' : 'text-gray-900')}>{value}</h3>
          <p className="text-sm text-gray-500 mt-1 font-medium">{title}</p>
          {description && <p className="text-xs text-gray-400 mt-1">{description}</p>}
        </div>
      </CardContent>
    </Card>
  );

  if (href) return <Link to={href}>{content}</Link>;
  return content;
}

export function Dashboard() {
  const { data: pedidosData } = useDashboardPedidos();
  const { data: producaoData } = useEstatisticasProducao();

  const totalFaturamento = Number(pedidosData?.totalFaturamento || 0);
  const totalLucro = Number(pedidosData?.totalLucro || 0);
  const margem = totalFaturamento > 0 ? (totalLucro / totalFaturamento) * 100 : 0;

  const pipeline = [
    { label: 'Novos', status: 'NOVO', color: 'bg-blue-500', textColor: 'text-blue-700', bg: 'bg-blue-50' },
    { label: 'Pago/Conf.', status: 'PAGO_CONFIRMADO', color: 'bg-indigo-500', textColor: 'text-indigo-700', bg: 'bg-indigo-50' },
    { label: 'Produção', status: 'PRODUCAO', color: 'bg-amber-500', textColor: 'text-amber-700', bg: 'bg-amber-50' },
    { label: 'A Separar', status: 'AGUARDANDO_SEPARACAO', color: 'bg-orange-500', textColor: 'text-orange-700', bg: 'bg-orange-50' },
    { label: 'Pronto/NF', status: 'PRONTO_EXPEDICAO', color: 'bg-emerald-500', textColor: 'text-emerald-700', bg: 'bg-emerald-50' },
    { label: 'Enviados', status: 'ENVIADO', color: 'bg-gray-400', textColor: 'text-gray-600', bg: 'bg-gray-50' },
  ];

  const alerta_margem = margem < 20;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Resumo operacional e financeiro do negócio</p>
        </div>
        <span className="text-xs text-gray-400 bg-gray-50 px-3 py-1.5 rounded-full font-medium border border-gray-100">
          {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
        </span>
      </div>

      {/* Alerta de margem baixa */}
      {alerta_margem && totalFaturamento > 0 && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <div>
            <p className="font-bold text-sm">Margem abaixo de 20%!</p>
            <p className="text-xs text-red-600">Verifique a precificação dos produtos. Margem atual: {margem.toFixed(1)}%</p>
          </div>
          <Link to="/admin/produtos" className="ml-auto text-xs font-bold underline">Ver produtos →</Link>
        </div>
      )}

      {/* Pipeline de Fluxo */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Pipeline de Operação</h2>
          <Link to="/admin/pedidos" className="text-xs text-primary font-bold hover:underline flex items-center gap-1">
            Ver Kanban <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {pipeline.map((item) => {
            const count = (pedidosData as any)?.porStatus?.find((s: any) => s.status === item.status)?._count?.id || 0;
            return (
              <Link key={item.status} to={`/admin/pedidos?status=${item.status}`}>
                <div className={cn(
                  'rounded-2xl border p-4 flex flex-col gap-2 hover:shadow-md transition-all group',
                  item.bg, count > 0 ? 'border-current/20' : 'border-gray-100'
                )}>
                  <div className={cn('w-2 h-2 rounded-full', item.color)} />
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">{item.label}</p>
                  <div className="flex items-end justify-between">
                    <h4 className={cn('text-3xl font-black', count > 0 ? item.textColor : 'text-gray-200')}>
                      {count}
                    </h4>
                    <ArrowUpRight className={cn('w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity', item.textColor)} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Stats financeiros */}
      <div>
        <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Financeiro do Mês</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Faturamento"
            value={formatCurrency(totalFaturamento)}
            description="Total de vendas no mês"
            icon={DollarSign}
            iconBg="bg-gradient-to-br from-primary to-primary/80"
            href="/admin/pedidos"
            highlight
          />
          <StatCard
            title="Lucro Líquido"
            value={formatCurrency(totalLucro)}
            description="Após custos de produção"
            trend={totalLucro > 0 ? 'up' : 'down'}
            trendValue={margem > 0 ? `${margem.toFixed(1)}% margem` : undefined}
            icon={TrendingUp}
            iconBg={totalLucro > 0 ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' : 'bg-gradient-to-br from-red-500 to-red-600'}
          />
          <StatCard
            title="Pedidos"
            value={pedidosData?.totalPedidosMes || 0}
            description="Pedidos neste mês"
            icon={ShoppingBag}
            iconBg="bg-gradient-to-br from-blue-500 to-blue-600"
            href="/admin/pedidos"
          />
          <StatCard
            title="Fila de Produção"
            value={producaoData?.totalOrdens || 0}
            description="Ordens ativas"
            icon={Printer}
            iconBg="bg-gradient-to-br from-amber-500 to-amber-600"
            href="/admin/producao"
          />
        </div>
      </div>

      {/* BI: Lucratividade e Metas */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Margem de lucro */}
        <Card className="border-gray-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Saúde Financeira</CardTitle>
              <BarChart3 className="w-5 h-5 text-gray-300" />
            </div>
            <CardDescription>Indicadores críticos de rentabilidade</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Margem */}
            <div>
              <div className="flex justify-between text-sm mb-1.5">
                <span className="font-medium text-gray-700">Margem de Lucro</span>
                <span className={cn('font-bold', margem >= 30 ? 'text-emerald-600' : margem >= 20 ? 'text-amber-600' : 'text-red-600')}>
                  {formatPercentage(margem)}
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className={cn('rounded-full h-2 transition-all', margem >= 30 ? 'bg-emerald-500' : margem >= 20 ? 'bg-amber-500' : 'bg-red-500')}
                  style={{ width: `${Math.min(margem, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Meta: 30% · {margem >= 30 ? '✅ Atingida' : `Faltam ${(30 - margem).toFixed(1)}pp`}</p>
            </div>

            {/* Taxa de refugo */}
            <div>
              <div className="flex justify-between text-sm mb-1.5">
                <span className="font-medium text-gray-700">Taxa de Refugo</span>
                <span className={cn('font-bold', (producaoData?.taxaRefugo || 0) <= 5 ? 'text-emerald-600' : 'text-red-600')}>
                  {(producaoData?.taxaRefugo || 0).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className={cn('rounded-full h-2', (producaoData?.taxaRefugo || 0) <= 5 ? 'bg-emerald-500' : 'bg-red-500')}
                  style={{ width: `${Math.min(producaoData?.taxaRefugo || 0, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Meta: &lt;5% · {(producaoData?.taxaRefugo || 0) <= 5 ? '✅ OK' : '⚠️ Acima do ideal'}</p>
            </div>

            {/* Status geral */}
            <div className={cn(
              'flex items-center gap-3 p-3 rounded-xl border',
              (margem >= 20 && (producaoData?.taxaRefugo || 0) <= 5) ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-amber-50 border-amber-100 text-amber-700'
            )}>
              {(margem >= 20 && (producaoData?.taxaRefugo || 0) <= 5)
                ? <CheckCircle className="w-5 h-5 shrink-0" />
                : <AlertTriangle className="w-5 h-5 shrink-0" />
              }
              <p className="text-xs font-medium">
                {(margem >= 20 && (producaoData?.taxaRefugo || 0) <= 5)
                  ? 'Operação saudável. Continue assim!'
                  : 'Atenção: há indicadores abaixo do ideal.'
                }
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Distribuição por origem */}
        <Card className="border-gray-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Vendas por Canal</CardTitle>
              <Activity className="w-5 h-5 text-gray-300" />
            </div>
            <CardDescription>Contribuição de cada marketplace</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(['SITE', 'MERCADOLIVRE', 'SHOPEE', 'MANUAL'] as const).map((origem) => {
              const valor = Number((pedidosData as any)?.porOrigem?.find((o: any) => o.origem === origem)?._sum?.faturamentoTotal || 0);
              const percentual = totalFaturamento > 0 ? (valor / totalFaturamento) * 100 : 0;
              const colors: Record<string, string> = {
                SITE: 'bg-blue-500', MERCADOLIVRE: 'bg-yellow-400', SHOPEE: 'bg-orange-500', MANUAL: 'bg-gray-400'
              };
              return (
                <div key={origem}>
                  <div className="flex justify-between text-xs mb-1 font-medium">
                    <span className="text-gray-600">{origem}</span>
                    <span className="text-gray-800 font-bold">{formatCurrency(valor)}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className={cn('rounded-full h-1.5', colors[origem])} style={{ width: `${percentual}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Ações Rápidas */}
        <Card className="border-gray-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Ações Rápidas</CardTitle>
              <Zap className="w-5 h-5 text-gray-300" />
            </div>
            <CardDescription>Atalhos para tarefas comuns</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {[
              { to: '/admin/produtos/novo', icon: Package, title: 'Novo Produto', desc: 'Cadastrar item no catálogo', color: 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white' },
              { to: '/admin/producao', icon: Printer, title: 'Fila de Impressão', desc: 'Gerenciar ordens de produção', color: 'bg-amber-50 text-amber-600 group-hover:bg-amber-500 group-hover:text-white' },
              { to: '/admin/pedidos', icon: Target, title: 'Pipeline Kanban', desc: 'Avançar pedidos um a um', color: 'bg-blue-50 text-blue-600 group-hover:bg-blue-500 group-hover:text-white' },
              { to: '/admin/cupons', icon: DollarSign, title: 'Criar Cupom', desc: 'Promoção de desconto', color: 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white' },
            ].map((item) => (
              <Link key={item.to} to={item.to} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group">
                <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center transition-all shrink-0', item.color)}>
                  <item.icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
                  <p className="text-xs text-gray-500 truncate">{item.desc}</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-gray-600 transition-colors shrink-0" />
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
